import React, { createContext, useContext, useEffect, useState } from "react";
import {
  checkAuth,
  login,
  phoneAuth,
  logout,
  register,
  sendResetCodeApi,
  verifyResetCodeApi,
  resetPasswordApi,
} from "../api/auth.api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    let mounted = true;

    checkAuth()
      .then((data) => {
        if (!mounted) return;
        setUser(data);
        setIsAuthenticated(true);
        setIsMember(data.is_member);
      })
      .catch(() => {
        if (!mounted) return;
        setUser(null);
        setIsAuthenticated(false);
        setIsMember(false);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const signIn = async (email, password, rememberMe) => {
    try {
      const data = await login({
        email,
        password,
        remember_me: rememberMe,
      });

      setIsAuthenticated(true);
      setIsMember(data.membership);
      setUser(data.user || null);

      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.message || "Invalid credentials",
      };
    }
  };

  const signInWithPhone = async (idToken) => {
    try {
      await phoneAuth(idToken);
      const session = await checkAuth();

      setIsAuthenticated(true);
      setIsMember(Boolean(session?.is_member));
      setUser(session || null);

      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.message || "Phone authentication failed",
      };
    }
  };

  const signOut = async () => {
    try {
      await logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setIsMember(false);
    }
  };

  const signUp = async (payload) => {
    try {
      await register(payload);
      return { success: true };
    } catch (err) {
      const rawMessage = (err?.message || "").toLowerCase();
      let friendlyMessage = err.message || "Signup failed";

      if (
        rawMessage.includes("first name") &&
        rawMessage.includes("email") &&
        rawMessage.includes("phone") &&
        rawMessage.includes("password")
      ) {
        friendlyMessage = "Please enter first name, email, phone number, and password.";
      } else if (rawMessage.includes("email already registered")) {
        friendlyMessage = "This email is already registered. Please sign in or use another email.";
      } else if (rawMessage.includes("phone number already registered")) {
        friendlyMessage = "This phone number is already registered. Please sign in or use another number.";
      }

      return {
        success: false,
        message: friendlyMessage,
      };
    }
  };

  const sendResetCode = async (email) => {
    try {
      await sendResetCodeApi(email);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.message || "Failed to send code",
      };
    }
  };

  const verifyResetCode = async (email, code) => {
    try {
      await verifyResetCodeApi(email, code);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.message || "Invalid code",
      };
    }
  };

  const resetPassword = async (email, code, password) => {
    try {
      await resetPasswordApi(email, code, password);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.message || "Reset failed",
      };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isMember,
        loading,
        showLoginModal,
        setShowLoginModal,
        signIn,
        signInWithPhone,
        signUp,
        signOut,
        sendResetCode,
        verifyResetCode,
        resetPassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
